import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, CreditCard, CheckCircle, ArrowLeft, Brain, Users, Trophy, Sparkles, ChevronRight, Zap, Smile, Star } from 'lucide-react';
import { mainAdminApi, userApi } from '../../../config';
import { SEO } from '../../SEO';

interface DmitTestSession {
    _id: string;
    timing: string;
    date: string;
    price: number;
}

const DmitTest: React.FC = () => {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState<DmitTestSession[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [userDetails, setUserDetails] = useState<any>(null);
    const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

    // Fetch User Details for potential booking logic
    const fetchUserDetails = async () => {
        try {
            const token = sessionStorage.getItem("userAccessToken");
            if (token) {
                const res = await userApi.get<{user: any}>('/profile', { headers: { Authorization: `Bearer ${token}` } });
                setUserDetails(res.data.user);
            }
        } catch (error) {
            console.error('Failed to fetch user details', error);
        }
    };

    const fetchDmitSessions = async () => {
        try {
            const response: any = await mainAdminApi.get('/view-dmit-tests');
            const data = response.data.dmitTests || [];
            setSessions(data);
        } catch (err) {
            console.error("Failed to fetch DMIT tests", err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        const token = sessionStorage.getItem("userAccessToken");
        setIsLoggedIn(!!token);
        fetchUserDetails();
        fetchDmitSessions();
    }, []);

    // Framer Motion Variants
    const fadeIn = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
    };

    const stagger = {
        visible: { transition: { staggerChildren: 0.1 } }
    };

    return (
        <div className="min-h-screen bg-[#F8F7F4] font-sans text-gray-800">
            <SEO
                title="DMIT Brain Mapping Test & Dermatoglyphics Analysis | MetaFit Wellness"
                description="Discover your innate talents, multiple intelligences, and learning potential through advanced DMIT dermatoglyphics brain mapping analysis at MetaFit Wellness."
                canonical="/Dmit-tests"
                keywords="DMIT test, dermatoglyphics, brain mapping, multiple intelligence, innate talent assessment, MetaFit Wellness"
            />
            {/* Header / Navigation */}
            <div className="bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
                    <Link to="/" className="inline-flex items-center text-teal-600 hover:text-teal-700 font-semibold transition-all gap-2 group">
                        <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" /> 
                        <span>Back to Home</span>
                    </Link>
                </div>
            </div>

            {/* Hero Section */}
            <motion.section 
                className="relative bg-contain bg-no-repeat bg-center py-24 sm:py-32 px-4 overflow-hidden bg-teal-900"
                style={{ backgroundImage: "url('/Dmit1.png')" }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                <div className="absolute inset-0 bg-black/60"></div>
                
                <motion.div 
                    className="relative max-w-5xl mx-auto text-center z-10"
                    initial="hidden"
                    animate="visible"
                    variants={fadeIn}
                >
                    <div className="inline-flex items-center gap-2 bg-teal-500/20 border border-teal-400/30 text-teal-200 px-4 py-2 rounded-full text-xs sm:text-sm uppercase tracking-wider mb-6">
                        <Sparkles className="w-4 h-4 text-teal-300" />
                        <span>Advanced Brain Mapping Analysis</span>
                    </div>
                    <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight leading-tight mb-6">
                        Unlock Your DNA’s <span className="text-teal-400">Hidden Blueprint</span>
                    </h1>
                    <p className="text-lg md:text-xl text-teal-50 mb-10 max-w-2xl mx-auto">
                        Understand your innate talents, personality traits, and learning styles through the scientific study of fingerprints.
                    </p>
                    <a href="#slots" className="bg-teal-500 hover:bg-teal-600 text-white font-bold py-4 px-10 rounded-full transition duration-300 transform hover:scale-105 inline-flex items-center text-lg shadow-xl">
                        Schedule Your Test
                        <ChevronRight className="ml-2 h-5 w-5" />
                    </a>
                </motion.div>
            </motion.section>

            {/* Introduction Section */}
            <section className="py-24 px-4 bg-white">
                <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
                    <motion.div 
                        initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}
                        className="relative"
                    >
                        <div className="absolute -top-4 -left-4 w-24 h-24 bg-teal-100 rounded-full -z-0"></div>
                        <img 
                            src="/Dmit2.png" 
                            alt="Brain Science"
                            className="rounded-2xl shadow-2xl relative z-10"
                        />
                    </motion.div>
                    <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeIn}>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">What is DMIT?</h2>
                        <p className="text-gray-600 mb-6 text-lg leading-relaxed">
                            DMIT is based on Genetics and Embryology. Fingerprints start developing in the womb from the 13th to 21st week, simultaneously with the brain. 
                        </p>
                        <div className="space-y-4">
                            {[
                                { icon: <Brain />, text: 'Identify Left/Right Brain dominance' },
                                { icon: <Users />, text: 'Understand social & emotional quotient' },
                                { icon: <Trophy />, text: 'Discover multiple intelligence profiles' }
                            ].map((item, idx) => (
                                <div key={idx} className="flex items-center gap-4 text-gray-700">
                                    <div className="bg-teal-100 p-2 rounded-lg text-teal-600">{item.icon}</div>
                                    <span className="font-medium">{item.text}</span>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-24 px-4 bg-[#F8F7F4]">
                <div className="max-w-7xl mx-auto">
                    <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" variants={fadeIn} viewport={{ once: true }}>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Why Choose DMIT Test?</h2>
                        <p className="text-gray-600 mt-4 max-w-2xl mx-auto text-lg">Uncover the scientific reality behind your natural inclinations.</p>
                    </motion.div>
                    <motion.div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" initial="hidden" whileInView="visible" variants={stagger} viewport={{ once: true }}>
                        {[
                            { icon: <Zap />, title: 'Innate Potential', text: 'Know your child’s or your own natural strengths from birth.' },
                            { icon: <Smile />, title: 'Relationship Harmony', text: 'Improve communication by understanding personality types.' },
                            { icon: <Star />, text: 'Stream Selection', title: 'Choose the right career path based on biological data.' }
                        ].map((item, i) => (
                            <motion.div key={i} variants={fadeIn} className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100">
                                <div className="bg-teal-50 text-teal-600 p-4 rounded-xl inline-block mb-4">{item.icon}</div>
                                <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                                <p className="text-gray-600">{item.text}</p>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Available Slots Section */}
            <section id="slots" className="py-24 px-4 bg-white">
                <div className="max-w-7xl mx-auto">
                    <motion.div className="text-center mb-16" initial="hidden" whileInView="visible" variants={fadeIn} viewport={{ once: true }}>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 flex items-center justify-center gap-3">
                            <Calendar className="text-teal-600" /> Book Your Slot
                        </h2>
                    </motion.div>

                    {isLoading ? (
                        <div className="flex justify-center items-center py-20"><Zap className="animate-spin text-teal-500 w-10 h-10" /></div>
                    ) : sessions.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {sessions.map((session, idx) => (
                                <motion.div 
                                    key={session._id}
                                    initial={{ opacity: 0, scale: 0.9 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="bg-white rounded-3xl p-8 border-2 border-teal-50 hover:border-teal-500 transition-all shadow-lg group relative overflow-hidden"
                                >
                                    <div className="absolute top-0 right-0 bg-teal-500 text-white px-6 py-2 rounded-bl-3xl font-bold">
                                        ₹{session.price}
                                    </div>
                                    <div className="flex flex-col gap-4 mt-4">
                                        <div className="flex items-center gap-4 text-gray-700">
                                            <Calendar className="text-teal-600 w-6 h-6" />
                                            <span className="text-lg font-semibold">{new Date(session.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                                        </div>
                                        <div className="flex items-center gap-4 text-gray-700">
                                            <Clock className="text-teal-600 w-6 h-6" />
                                            <span className="text-lg">{session.timing}</span>
                                        </div>
                                    </div>
                                    {isLoggedIn ? (
                                        <button 
                                            onClick={() => navigate('/dmit-tests/booking', { state: { sessionData: session, userDetails } })}
                                            className="w-full mt-8 bg-gray-900 text-white py-4 rounded-2xl font-bold hover:bg-teal-600 transition-all flex items-center justify-center gap-2 group-hover:shadow-lg transform active:scale-95">
                                            <CreditCard className="w-5 h-5" /> Confirm Booking
                                        </button>
                                    ) : (
                                        <button 
                                            onClick={() => navigate('/login')}
                                            className="w-full mt-8 bg-gray-900 text-white py-4 rounded-2xl font-bold hover:bg-teal-600 transition-all flex items-center justify-center gap-2 group-hover:shadow-lg transform active:scale-95">
                                            <Users className="w-5 h-5" /> Login to Book
                                        </button>
                                    )}
                                </motion.div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-20 bg-gray-50 rounded-3xl border-2 border-dashed border-gray-200">
                            <h3 className="text-2xl font-bold text-gray-400">New Slots Coming Soon</h3>
                        </div>
                    )}
                </div>
            </section>

            {/* Testimonial Section */}
            <section className="bg-teal-800 py-24 px-4 text-white">
                <motion.div className="max-w-3xl mx-auto text-center" initial="hidden" whileInView="visible" variants={fadeIn}>
                    <div className="flex justify-center mb-6">
                        {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="fill-teal-400 text-teal-400 w-6 h-6" />)}
                    </div>
                    <p className="text-2xl font-light italic leading-relaxed mb-6">
                        "The DMIT test revealed strengths I always felt I had but never knew how to focus on. It has been a roadmap for my career transition."
                    </p>
                    <h4 className="text-xl font-bold text-teal-200">- Aman Sharma</h4>
                </motion.div>
            </section>
        </div>
    );
};

export default DmitTest;